import React, { useEffect, useRef, useState } from "react";
import {
  DriverProfileSummary,
  DriverQualificationPanel,
} from "./DriverCard";
import DriverPayouts from "./Payouts/DriverPayouts";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGetDriverQuery,
  useUpdateDriverMutation,
} from "../../../redux/services/drivers/driversApi";
import { SidebarContainer } from "../../../components";
import Tabs from "../../../shared/Tabs";
import DriverManifests from "./Manifests/DriverManifests";
import { useCrumbs } from "../../../hooks/useCrumbs";
import JobChart from "./JobChart";
import { Button, Card, EditableText, Spinner } from "../../../shared";
import request from "../../../utils/request";
import { driverJobsStage } from "../../../constants/aggregate";
import DriverReports from "./Reports/DriverReports";

const DriverShow = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const crumbs = useCrumbs();

  const {
    data: driverResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetDriverQuery(params.id, {
    skip: !/^[0-9a-fA-F]{24}$/.test(String(params.id || "")),
  });
  const [updateDriver] = useUpdateDriverMutation();

  const driver = driverResponse?.user || null;
  const driverStatus = isLoading || isFetching ? "loading" : "succeeded";
  const fullName = driver?.fullName;

  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    if (params.id) {
      crumbs.setLoading(isLoading);
    }
  }, [params.id, isLoading, crumbs]);

  useEffect(() => {
    if (driver?._id) {
      fetchReport();

      crumbs.updateCrumbName({
        name: driver.fullName,
        path: "/drivers/" + driver._id,
      });
      crumbs.setLoading(false);
      return;
    }
    if (!isLoading) {
      crumbs.setLoading(false);
    }
  }, [driver?._id, driver?.fullName, isLoading]);

  async function handleUpdate(update) {
    await updateDriver({
      id: params.id,
      data: update,
    }).unwrap();
  }

  const fetchReport = async () => {
    const stages = driverJobsStage(
      "appointmentDate",
      user.parentCompany,
      driver._id
    );

    const response = await request(`/reports`, {
      method: "post",
      data: {
        stages,
        modelName: "Leg",
        config: {
          0: {
            type: "$match",
            _id: "driver",
          },
        },
      },
    });

    setReportData(response?.data || []);
  };

  const headerRef = useRef();
  const [headerContext, setHeaderContext] = useState();
  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !driver?._id) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">
            Unable to load this driver.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            The driver was not found or the id is invalid.
          </p>
          <Button
            className="mt-4"
            type="alternative"
            onClick={() => navigate("/drivers")}
          >
            Return to drivers
          </Button>
        </div>
      </div>
    );
  }

  const renderHeader = (headerProps) => {
    return (
      <div ref={headerRef} className="border-b bg-white p-2">
        {headerProps.title}
      </div>
    );
  };

  const tabs = [
    {
      id: "Info",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-4 xl:col-span-3">
              <DriverProfileSummary
                info={driver}
                handleUpdate={handleUpdate}
              />
            </div>
            <div className="min-w-0 lg:col-span-8 xl:col-span-9">
              <Card className="p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-700">
                  Leg Activity
                </h3>
                <JobChart data={reportData} />
              </Card>
            </div>
          </div>
          <DriverQualificationPanel
            info={driver}
            handleUpdate={handleUpdate}
          />
        </div>
      ),
    },
    {
      id: "Payouts",
      content: (
        <div className="h-full min-h-0">
          <DriverPayouts driver={driver} driverStatus={driverStatus} />
        </div>
      ),
    },
    {
      id: "Manifests",
      content: (
        <div className="h-auto">
          <DriverManifests driver={driver} driverStatus={driverStatus} />
        </div>
      ),
    },
    {
      id: "Reports",
      content: (
        <div className="h-auto">
          <DriverReports driver={driver} driverStatus={driverStatus} />
        </div>
      ),
    },
  ];

  const title = (
    <div className="flex items-center text-lg font-semibold text-gray-800">
      <EditableText
        className="ml-1"
        iconClasses="h-3 w-3"
        id="fullName"
        value={fullName}
        size="sm"
        displayText={(v) => (
          <span className="text-lg font-semibold text-gray-800">{v}</span>
        )}
        onSave={(e) => handleUpdate({ fullName: e.value })}
      />
    </div>
  );

  return (
    <SidebarContainer title={title} header={renderHeader} hideSidebar>
      {({ parentHeight }) => {
        const offsetTop = headerContext?.offsetTop;
        const offsetHeight = headerContext?.offsetHeight;
        const wrapperStyle = { height: `${parentHeight - offsetTop}px` };
        const panelStyle = {
          height: `${parentHeight - offsetTop - offsetHeight - 70}px`,
        };
        return (
          <Tabs
            className="w-full overflow-hidden"
            fullHeight
            style={wrapperStyle}
            panelStyle={panelStyle}
            useSearchParams
            panelClasses="min-h-0 overflow-y-auto border-t p-2"
            tabListClasses="h-[38px] bg-white px-2"
            tabclassName="mx-1"
            id="driver-show"
            data={tabs}
          />
        );
      }}
    </SidebarContainer>
  );
};

export default DriverShow;
